/**
 * InvoiceItemForm Component - T101
 *
 * Modal form for adding/editing invoice items
 */
import { useState, type FormEvent } from 'react';
import { GlassPanel, Input, Select, Button } from '../ui';
import { unitTypeLabels, type InvoiceItem, type UnitType, type DiscountType } from '../../hooks/useInvoices';
import type { CreateInvoiceItemInput } from '@armard/shared';

interface InvoiceItemFormProps {
  item?: InvoiceItem;
  onSubmit: (data: CreateInvoiceItemInput) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const unitTypeOptions = Object.entries(unitTypeLabels).map(([value, label]) => ({
  value,
  label,
}));

const discountTypeOptions = [
  { value: '', label: 'Kein Rabatt' },
  { value: 'percent', label: 'Prozent (%)' },
  { value: 'absolute', label: 'Betrag (€)' },
];

export function InvoiceItemForm({
  item,
  onSubmit,
  onCancel,
  isLoading,
}: InvoiceItemFormProps) {
  const [formData, setFormData] = useState({
    description: item?.description ?? '',
    quantity: item ? parseFloat(item.quantity) : 1,
    unitType: (item?.unitType ?? 'pieces') as UnitType,
    unitPrice: item ? parseFloat(item.unitPrice) : 0,
    discountType: (item?.discountType ?? '') as DiscountType | '',
    discountValue: item?.discountValue ? parseFloat(item.discountValue) : 0,
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    const data: CreateInvoiceItemInput = {
      description: formData.description,
      quantity: formData.quantity,
      unitType: formData.unitType,
      unitPrice: formData.unitPrice,
    };

    if (formData.discountType) {
      data.discountType = formData.discountType as DiscountType;
      data.discountValue = formData.discountValue;
    }

    onSubmit(data);
  };

  const lineTotal = formData.quantity * formData.unitPrice;
  let discountAmount = 0;
  if (formData.discountType === 'percent') {
    discountAmount = lineTotal * (formData.discountValue / 100);
  } else if (formData.discountType === 'absolute') {
    discountAmount = formData.discountValue;
  }
  const finalTotal = lineTotal - discountAmount;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <GlassPanel className="w-full max-w-lg">
        <form onSubmit={handleSubmit}>
          <h3 className="text-lg font-medium text-white mb-4">
            {item ? 'Position bearbeiten' : 'Neue Position'}
          </h3>

          <div className="space-y-4">
            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-white/70 mb-1.5">
                Beschreibung *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                rows={2}
                className="w-full px-4 py-2.5 rounded-lg bg-white/[0.03] border border-white/[0.08] text-white placeholder:text-white/30 focus:outline-none focus:border-white/20 focus:bg-white/[0.05] transition-colors resize-none"
                placeholder="Leistung beschreiben..."
              />
            </div>

            {/* Quantity and Unit Type */}
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Menge *"
                type="number"
                min={0.01}
                step={0.01}
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: parseFloat(e.target.value) || 0 })}
                required
              />
              <Select
                label="Einheit"
                options={unitTypeOptions}
                value={formData.unitType}
                onChange={(e) => setFormData({ ...formData, unitType: e.target.value as UnitType })}
              />
            </div>

            {/* Unit Price */}
            <Input
              label="Einzelpreis (€) *"
              type="number"
              step={0.01}
              value={formData.unitPrice}
              onChange={(e) => setFormData({ ...formData, unitPrice: parseFloat(e.target.value) || 0 })}
              required
            />

            {/* Discount */}
            <div className="grid grid-cols-2 gap-4">
              <Select
                label="Rabatt"
                options={discountTypeOptions}
                value={formData.discountType}
                onChange={(e) => setFormData({ ...formData, discountType: e.target.value as DiscountType | '' })}
              />
              {formData.discountType && (
                <Input
                  label={formData.discountType === 'percent' ? 'Rabatt (%)' : 'Rabatt (€)'}
                  type="number"
                  min={0}
                  max={formData.discountType === 'percent' ? 100 : lineTotal}
                  step={0.01}
                  value={formData.discountValue}
                  onChange={(e) => setFormData({ ...formData, discountValue: parseFloat(e.target.value) || 0 })}
                />
              )}
            </div>

            {/* Preview Total */}
            <div className="pt-4 border-t border-white/10">
              <div className="flex justify-between text-sm">
                <span className="text-white/50">Zwischensumme:</span>
                <span className="text-white">{lineTotal.toFixed(2)} €</span>
              </div>
              {discountAmount > 0 && (
                <div className="flex justify-between text-sm text-orange-400">
                  <span>Rabatt:</span>
                  <span>-{discountAmount.toFixed(2)} €</span>
                </div>
              )}
              <div className="flex justify-between text-base font-medium mt-1">
                <span className="text-white/70">Gesamt:</span>
                <span className="text-white">{finalTotal.toFixed(2)} €</span>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <Button type="button" variant="secondary" onClick={onCancel}>
              Abbrechen
            </Button>
            <Button type="submit" loading={isLoading}>
              {item ? 'Speichern' : 'Hinzufügen'}
            </Button>
          </div>
        </form>
      </GlassPanel>
    </div>
  );
}
